import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  FeatureFlagKey,
  IFeatureFlagMap,
  IResolvedFeatureFlag,
} from "../../interfaces/featureFlag.interface";
import {
  getFeatureFlagsFromServer,
  updateFeatureFlagOnServer,
} from "./featureFlagsApi";

export interface FeatureFlagsState {
  flags: IFeatureFlagMap | null;
  loading: boolean;
  error?: string;
  /** Keys with a flip in flight, so only the flipped row shows a spinner. */
  updatingKeys: string[];
}

const initialState: FeatureFlagsState = {
  flags: null,
  loading: false,
  error: undefined,
  updatingKeys: [],
};

const FEATURE_FLAGS_MAX_ATTEMPTS = 3;

export const fetchFeatureFlags = createAsyncThunk(
  "featureFlags/fetchFeatureFlags",
  async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt < FEATURE_FLAGS_MAX_ATTEMPTS; attempt++) {
      try {
        return await getFeatureFlagsFromServer();
      } catch (err) {
        lastError = err;
        if (attempt < FEATURE_FLAGS_MAX_ATTEMPTS - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1))
          );
        }
      }
    }
    throw lastError;
  },
  {
    // Skip when already loaded, in flight, or permanently failed this session.
    condition: (_, { getState }) => {
      const { flags, loading, error } = (getState() as RootState).featureFlags;
      return flags == null && !loading && error == null;
    },
  }
);

export interface FeatureFlagUpdateError {
  status?: number;
  message: string;
}

export const updateFeatureFlag = createAsyncThunk<
  IResolvedFeatureFlag,
  { key: string; enabled: boolean },
  { rejectValue: FeatureFlagUpdateError }
>("featureFlags/updateFeatureFlag", async (params, { rejectWithValue }) => {
  try {
    return await updateFeatureFlagOnServer(params);
  } catch (err) {
    // createAsyncThunk serializes a thrown error and drops `response`, so the
    // status has to travel as an explicit payload for the 403 to be readable.
    const error = err as {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };
    return rejectWithValue({
      status: error?.response?.status,
      message:
        error?.response?.data?.message ||
        error?.message ||
        `Failed to update ${params.key}`,
    });
  }
});

const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    resetFeatureFlagsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.loading = false;
        state.flags = action.payload;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateFeatureFlag.pending, (state, action) => {
        state.updatingKeys.push(action.meta.arg.key);
      })
      .addCase(updateFeatureFlag.fulfilled, (state, action) => {
        const { key } = action.meta.arg;
        state.updatingKeys = state.updatingKeys.filter((k) => k !== key);
        // The response is the resolved post-image, so the row reflects the
        // server's view (including `source` flipping to "database").
        if (state.flags) state.flags[key] = action.payload;
      })
      .addCase(updateFeatureFlag.rejected, (state, action) => {
        state.updatingKeys = state.updatingKeys.filter(
          (k) => k !== action.meta.arg.key
        );
      });
  },
});

export const { resetFeatureFlagsState } = featureFlagsSlice.actions;

// Registry default is enabled (hidden). Until the GET returns, keep Amex hidden.
export const selectHideInPersonAmex = (state: RootState): boolean =>
  state.featureFlags.flags?.[FeatureFlagKey.HideInPersonAmex]?.enabled ?? true;

// Same fail-closed rule: an unreleased acquirer stays hidden until the server
// says otherwise, so a failed read can never expose it.
export const selectHideUnreleasedAcquirers = (state: RootState): boolean =>
  state.featureFlags.flags?.[FeatureFlagKey.HideUnreleasedAcquirers]?.enabled ??
  true;

export const selectFeatureFlags = (state: RootState) =>
  state.featureFlags.flags;

export const selectFeatureFlagsLoading = (state: RootState): boolean =>
  state.featureFlags.loading;

export const selectFeatureFlagsError = (state: RootState): string | undefined =>
  state.featureFlags.error;

export const selectUpdatingFeatureFlagKeys = (state: RootState): string[] =>
  state.featureFlags.updatingKeys;

export default featureFlagsSlice.reducer;
